import { IconInfoCircleFilled } from '@iwoca/icons';
import _ from 'lodash';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { FormattedCurrency } from '../../../iwIntlProvider/FormattedCurrency';
import IwMedia from '../../../iwMedia/IwMedia';
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow
} from '../../../myAccount/ui/elements/Table/Table';

export const RepaymentScheduleTable = ({
    hasFees,
    totalPrincipal,
    totalInterest,
    totalRepayment,
    totalFees,
    repayments
}: {
    hasFees: boolean;
    totalPrincipal: number;
    totalInterest: number;
    totalRepayment: number;
    totalFees: number;
    repayments: {
        date: string;
        interest: number;
        principal: number;
        total: number;
        total_fees: number;
    }[];
}) => {
    const getTableData = () => {
        const rows = _.map(repayments, (repayment, index) => {
            return (
                <TableRow key={index} data-testid="repayment-row">
                    <IwMedia to="small">
                        <TableCell className={'align-top'}>
                            <FormattedDate
                                value={new Date(repayment.date)}
                                day="numeric"
                                month="numeric"
                                year="2-digit"
                            />
                        </TableCell>
                        <TableCell colSpan={2}>
                            <span className={'pr-xs'}>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.principal"
                                    defaultMessage="Principal"
                                />
                                :
                            </span>
                            <FormattedCurrency value={repayment.principal} />
                            <br />
                            <span className={'pr-xs'}>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.interest"
                                    defaultMessage="Interest"
                                />
                                :
                            </span>
                            <FormattedCurrency value={repayment.interest} />
                            {hasFees && (
                                <>
                                    <br />
                                    <span className={'pr-xs'}>
                                        <FormattedMessage
                                            id="_.modal.repayment_schedule.table.fees"
                                            defaultMessage="Fees"
                                        />
                                        :
                                    </span>
                                    <FormattedCurrency
                                        value={repayment.total_fees}
                                    />
                                </>
                            )}
                        </TableCell>
                        <TableCell className={'align-top'}>
                            <FormattedCurrency value={repayment.total} />
                        </TableCell>
                    </IwMedia>
                    <IwMedia from="small">
                        <TableCell>
                            <FormattedDate value={new Date(repayment.date)} />
                        </TableCell>
                        <TableCell>
                            <FormattedCurrency value={repayment.principal} />
                        </TableCell>
                        <TableCell>
                            <FormattedCurrency value={repayment.interest} />
                        </TableCell>
                        {hasFees && (
                            <TableCell>
                                <FormattedCurrency
                                    value={repayment.total_fees}
                                />
                            </TableCell>
                        )}
                        <TableCell>
                            <FormattedCurrency value={repayment.total} />
                        </TableCell>
                    </IwMedia>
                </TableRow>
            );
        });

        return rows;
    };

    return (
        <>
            <Table data-testid="repayment-schedule-table">
                <TableHeader>
                    <TableRow>
                        <IwMedia to="small">
                            <TableHead>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.date"
                                    defaultMessage="Date"
                                />
                            </TableHead>
                            <TableHead colSpan={2}>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.split"
                                    defaultMessage="Split"
                                />
                            </TableHead>
                            <TableHead>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.total"
                                    defaultMessage="Total"
                                />
                            </TableHead>
                        </IwMedia>

                        <IwMedia from="small">
                            <TableHead>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.date"
                                    defaultMessage="Date"
                                />
                            </TableHead>
                            <TableHead>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.principal"
                                    defaultMessage="Principal"
                                />
                            </TableHead>
                            <TableHead>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.interest"
                                    defaultMessage="Interest"
                                />
                            </TableHead>

                            {hasFees && (
                                <TableHead>
                                    <FormattedMessage
                                        id="_.modal.repayment_schedule.table.fees"
                                        defaultMessage="Fees"
                                    />
                                </TableHead>
                            )}

                            <TableHead>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.totalRepayment"
                                    defaultMessage="Total Repayment"
                                />
                            </TableHead>
                        </IwMedia>
                    </TableRow>
                </TableHeader>

                <TableBody>{getTableData()}</TableBody>

                <TableFooter>
                    <TableRow>
                        <IwMedia to="small">
                            <TableCell colSpan={3}>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.total"
                                    defaultMessage="Total"
                                />
                                :
                            </TableCell>
                        </IwMedia>

                        <IwMedia from="small">
                            <TableCell>
                                <FormattedMessage
                                    id="_.modal.repayment_schedule.table.total"
                                    defaultMessage="Total"
                                />
                                :
                            </TableCell>
                            <TableCell data-testid="total-principal">
                                <FormattedCurrency value={totalPrincipal} />
                            </TableCell>
                            <TableCell data-testid="total-interest">
                                <FormattedCurrency value={totalInterest} />
                            </TableCell>

                            {hasFees && (
                                <TableCell>
                                    <FormattedCurrency value={totalFees} />
                                </TableCell>
                            )}
                        </IwMedia>

                        <TableCell data-testid="total-balance">
                            <FormattedCurrency value={totalRepayment} />
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>

            {repayments.length < 12 && (
                <div className="mt-l flex gap-s rounded-m bg-primary-97 p-m text-s text-secondary-40">
                    <IconInfoCircleFilled className="mt-m shrink-0 text-xl text-secondary-40" />
                    <div>
                        <p className="m-0 text-s">
                            <FormattedMessage
                                id="_.modal.repayment_schedule.table.pay_off"
                                defaultMessage="To pay off your loan early, just make a bank transfer or a one-off payment in your iwoca account."
                            />
                        </p>
                        <p>
                            <FormattedMessage
                                id="_.modal.repayment_schedule.table.extra_payments"
                                defaultMessage="You can also make extra payments throughout to bring your outstanding amount down."
                            />
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};
